/**
 * Apply preset confirmation - campaign, texts, senders, work date
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, Megaphone, Wifi, Globe } from 'lucide-react';

import { Button, SearchableSelect } from '@/components/ui';
import { Input } from '@/components/ui';
import type { PresetsModalCatalog } from './shared';
import {
  filterSenderNamesMax11,
  MAX_SELECTED_TEXTS,
  uniqueKeepOrderStrings,
} from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';
import { useDefaultCampaign } from '@/features/smswiser-campaigns/hooks/useDefaultCampaign';
import { useToast } from '@/providers/ToastProvider';
import { ApplyRuleBasedCreativeSection } from './apply';
import type { Preset } from './shared';

export interface ApplyPresetModalProps {
  preset: Preset | null;
  workDate: string;
  campaignWiserId: number;
  campaigns: Array<{ id: number; name: string }>;
  onCampaignChange: (wiserId: number) => void;
  isBusy: boolean;
  countryKey?: string;
  catalog?: PresetsModalCatalog;
  smsTexts?: string[];
  senderNames?: string[];
  tagColors?: Record<string, string>;
  dropCount?: number;
  dropLabels?: Array<{ id: string; label: string }>;
  onClose: () => void;
  onAiGeneratingChange?: (isGenerating: boolean) => void;
  onConfirm: (
    workDate: string,
    campaignWiserId: number,
    opts: {
      smsTextsOverride?: string[];
      smsTextsPerDrop?: string[][];
      senderNamesOverride?: string[];
      senderNamesPerDrop?: string[][];
      routeId?: number;
      shortLinkTldValue?: string;
      andSend?: boolean;
    },
  ) => void;
  onWorkDateChange: (v: string) => void;
}

const MAX_SELECTED_SENDERS = 50;

export const ApplyPresetModal: FC<ApplyPresetModalProps> = ({
  preset,
  workDate,
  campaignWiserId,
  campaigns,
  onCampaignChange,
  isBusy,
  countryKey = '',
  catalog,
  smsTexts = [],
  senderNames = [],
  tagColors,
  dropCount,
  dropLabels,
  onClose,
  onAiGeneratingChange,
  onConfirm,
  onWorkDateChange,
}) => {
  const [selectedTexts, setSelectedTexts] = useState<string[]>([]);
  const [selectedSenders, setSelectedSenders] = useState<string[]>([]);
  const [perDropTexts, setPerDropTexts] = useState<string[][] | null>(null);
  const [perDropSenders, setPerDropSenders] = useState<string[][] | null>(null);
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const isRuleBased = preset?.presetType === 'rule-based';
  const gateways = catalog?.gateways ?? [];
  const tlds = catalog?.tlds ?? [];
  const { defaultCampaignId, setDefaultCampaign } = useDefaultCampaign(countryKey ?? '');
  const { success: toastSuccess } = useToast();

  const handleSaveAsDefault = () => {
    if (campaignWiserId <= 0) return;
    setDefaultCampaign(campaignWiserId);
    const name = campaigns.find((c) => c.id === campaignWiserId)?.name ?? 'Campaign';
    toastSuccess(`${name} saved as default`);
  };
  const templateTld = (preset?.template as { shortLinkTldValue?: string } | undefined)?.shortLinkTldValue?.trim();
  const initialTld =
    templateTld && tlds.some((t) => t.value === templateTld) ? templateTld : tlds[0]?.value ?? '';
  const [routeId, setRouteId] = useState<number>(gateways[0]?.id ?? 0);
  const [shortLinkTldValue, setShortLinkTldValue] = useState(initialTld);
  useEffect(() => {
    if (!isRuleBased || gateways.length === 0 || tlds.length === 0) return;
    setRouteId((prev) =>
      prev <= 0 || !gateways.some((g) => g.id === prev) ? gateways[0]!.id : prev,
    );
    const templateVal = (preset?.template as { shortLinkTldValue?: string } | undefined)?.shortLinkTldValue?.trim();
    const fallback = templateVal && tlds.some((t) => t.value === templateVal) ? templateVal : tlds[0]!.value;
    setShortLinkTldValue((prev) => (!prev || !tlds.some((t) => t.value === prev) ? fallback : prev));
  }, [isRuleBased, gateways, tlds, preset?.template]);

  const handleAiGeneratingChange = useCallback(
    (v: boolean) => {
      setIsAiGenerating(v);
      onAiGeneratingChange?.(v);
    },
    [onAiGeneratingChange],
  );

  const handleToggleText = useCallback((text: string) => {
    setSelectedTexts((prev) =>
      prev.includes(text) ? prev.filter((t) => t !== text) : prev.length >= MAX_SELECTED_TEXTS ? prev : [...prev, text],
    );
  }, []);

  const handleToggleSender = useCallback((sender: string) => {
    setSelectedSenders((prev) =>
      prev.includes(sender)
        ? prev.filter((s) => s !== sender)
        : prev.length >= MAX_SELECTED_SENDERS
          ? prev
          : [...prev, sender],
    );
  }, []);

  const handleSelectAllTexts = useCallback(() => {
    const cap = Math.min(smsTexts.length, MAX_SELECTED_TEXTS);
    setSelectedTexts((prev) =>
      prev.length >= cap ? [] : uniqueKeepOrderStrings(smsTexts).slice(0, MAX_SELECTED_TEXTS),
    );
  }, [smsTexts]);

  const handleSelectAllSenders = useCallback(() => {
    const cap = Math.min(senderNames.length, MAX_SELECTED_SENDERS);
    setSelectedSenders((prev) =>
      prev.length >= cap ? [] : filterSenderNamesMax11(uniqueKeepOrderStrings(senderNames)).slice(0, MAX_SELECTED_SENDERS),
    );
  }, [senderNames]);

  const smsTextsOverride = useMemo(
    () => (selectedTexts.length > 0 ? uniqueKeepOrderStrings(selectedTexts).slice(0, 50) : undefined),
    [selectedTexts],
  );

  const senderNamesOverride = useMemo(
    () =>
      selectedSenders.length > 0
        ? filterSenderNamesMax11(uniqueKeepOrderStrings(selectedSenders)).slice(0, 50)
        : undefined,
    [selectedSenders],
  );

  const handleConfirm = useCallback(
    (andSend?: boolean) => {
      const opts = {
        smsTextsOverride: perDropTexts?.length ? undefined : smsTextsOverride,
        smsTextsPerDrop: perDropTexts?.length ? perDropTexts : undefined,
        senderNamesOverride: perDropSenders?.length ? undefined : senderNamesOverride,
        senderNamesPerDrop: perDropSenders?.length ? perDropSenders : undefined,
        routeId: isRuleBased ? routeId : undefined,
        shortLinkTldValue: isRuleBased ? shortLinkTldValue : undefined,
        andSend,
      };
      onConfirm(workDate, campaignWiserId, opts);
    },
    [
      workDate,
      campaignWiserId,
      perDropTexts,
      perDropSenders,
      smsTextsOverride,
      senderNamesOverride,
      isRuleBased,
      routeId,
      shortLinkTldValue,
      onConfirm,
    ],
  );

  const canApply =
    workDate.trim().length > 0 &&
    campaignWiserId > 0 &&
    (!isRuleBased ||
      ((dropCount !== undefined && dropCount > 0) &&
        routeId > 0 &&
        shortLinkTldValue.trim().length > 0));

  if (!preset) return null;

  return (
    <div className="flex flex-col gap-5 p-4">
      <div className="flex flex-col gap-3">
        {campaigns.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-1">
              <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                <Megaphone className="h-3.5 w-3.5" />
                Campaign
              </label>
              {countryKey && (
                <span className="text-[10px]">
                  {campaignWiserId > 0 ? (
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={handleSaveAsDefault}
                    >
                      Save as default
                    </button>
                  ) : defaultCampaignId != null && campaigns.some((c) => c.id === defaultCampaignId) ? (
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() => onCampaignChange(defaultCampaignId)}
                    >
                      Apply default
                    </button>
                  ) : null}
                </span>
              )}
            </div>
            <SearchableSelect
              options={[
                { value: 0, label: 'You must select a campaign' },
                ...campaigns.map((c) => ({ value: c.id, label: c.name })),
              ]}
              value={campaignWiserId}
              onChange={(v) => onCampaignChange(Number(v))}
              placeholder="You must select a campaign"
            />
          </div>
        )}
        {isRuleBased && gateways.length > 0 && tlds.length > 0 && (
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1.5">
              <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                <Wifi className="h-3.5 w-3.5" />
                Gateway
              </label>
              <SearchableSelect
                options={gateways.map((g) => ({
                  value: g.id,
                  label: `${g.name}${g.pricePerSms != null ? ` ($${Number(g.pricePerSms).toFixed(3)}/SMS)` : ''}`,
                }))}
                value={routeId}
                onChange={(v) => setRouteId(Number(v))}
                placeholder="Select gateway"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                <Globe className="h-3.5 w-3.5" />
                TLD
              </label>
              <select
                value={shortLinkTldValue}
                onChange={(e) => setShortLinkTldValue(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              >
                {tlds.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.displayName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
        <p className="text-sm text-muted-foreground">
          Apply &quot;{preset.name}&quot; —{' '}
          {isRuleBased
            ? dropCount === undefined
              ? 'checking matching lists…'
              : dropCount === 0
                ? 'no matching lists'
                : 'one drop per matching list'
            : `${preset.items.length} drop${preset.items.length !== 1 ? 's' : ''} will be created`}
          .
        </p>
      </div>
      {countryKey && (dropCount ?? 0) > 0 && (
        <ApplyRuleBasedCreativeSection
          smsTexts={smsTexts}
          senderNames={senderNames}
          selectedTexts={selectedTexts}
          selectedSenders={selectedSenders}
          onToggleText={handleToggleText}
          onToggleSender={handleToggleSender}
          onSelectAllTexts={handleSelectAllTexts}
          onSelectAllSenders={handleSelectAllSenders}
          onTextsChange={setSelectedTexts}
          onSendersChange={setSelectedSenders}
          onPerDropTextsChange={setPerDropTexts}
          onPerDropSendersChange={setPerDropSenders}
          onAiGeneratingChange={handleAiGeneratingChange}
          countryKey={countryKey}
          dropCount={dropCount}
          dropLabels={dropLabels}
          tagColors={tagColors}
          isBusy={isBusy}
        />
      )}
      <div className="flex flex-col gap-2 rounded-lg border border-border/80 bg-muted/5 p-3">
        <label className="text-xs font-medium text-muted-foreground">Work date</label>
        <div className="relative">
          <Calendar className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="date"
            value={workDate}
            onChange={(e) => onWorkDateChange(e.target.value)}
            className="h-9 pl-9"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 border-t border-border/60 pt-4">
        <Button
          variant="outline"
          size="sm"
          onClick={onClose}
          disabled={isBusy || isAiGenerating}
          title={isAiGenerating ? 'Please wait for AI generation to complete' : undefined}
        >
          Cancel
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleConfirm(true)}
          disabled={!canApply || isBusy}
        >
          Create and send
        </Button>
        <Button
          size="sm"
          onClick={() => handleConfirm(false)}
          disabled={!canApply || isBusy}
        >
          Create drops
        </Button>
      </div>
    </div>
  );
};
